import { getGalleryMedia, startMediaDownload, getMediaDownloadStatus } from '@/app/services/albums/albums';
import * as Actions from '@/app/store/actions';
import { CircularProgress } from '@material-ui/core';
import {
	CheckBox,
	CheckBoxOutlineBlank,
	Search as SearchIcon,
	Add as AddIcon,
} from '@material-ui/icons';
import Pagination from '@material-ui/lab/Pagination';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import GalleryAlbums from './GalleryAlbums';
import GalleryFiltersBar from './GalleryFiltersBar';
import Lightbox from './Lightbox';
import usePreloadImages from './usePreloadImages';
import AddToAlbumModal from './AddToAlbumModal';
import CreateAlbumModal from './CreateAlbumModal';
import DownloadProgressModal from '@/app/customComponents/modals/DownloadProgressModal';
import './Gallery.css';

const Gallery = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const activeTab = location.pathname === '/gallery/albums' ? 'albums' : 'photos';
	const [media, setMedia] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [selectedMedia, setSelectedMedia] = useState(new Set());
	const [lightboxIndex, setLightboxIndex] = useState(null);
	const [addToAlbumOpen, setAddToAlbumOpen] = useState(false);
	const [createAlbumOpen, setCreateAlbumOpen] = useState(false);
	const [albumsCreateOpen, setAlbumsCreateOpen] = useState(false);
	const [albumSearchQuery, setAlbumSearchQuery] = useState('');
	const [downloadProgress, setDownloadProgress] = useState(0);
	const [downloadModalOpen, setDownloadModalOpen] = useState(false);
	const [downloadLink, setDownloadLink] = useState(null);

	const user = useSelector((state) => state.auth.user);
	const dispatch = useDispatch();
	const cache = useRef({});

	const [photoFilters, setPhotoFilters] = useState({
		room_id: [],
		student_id: [],
		activity_id: [],
		album_id: [],
		media_type: null,
		start_date: null,
		end_date: null,
	});

	const [albumFilters, setAlbumFilters] = useState({
		room_id: [],
		type: null,
	});

	const fetchMedia = useCallback(async () => {
		try {
			setLoading(true);
			const params = { page };

			if (photoFilters.room_id?.length > 0) {
				params.room_id = photoFilters.room_id.map((r) => r.value);
			}
			if (photoFilters.student_id?.length > 0) {
				params.student_id = photoFilters.student_id.map((s) => s.value);
			}
			if (photoFilters.activity_id?.length > 0) {
				params.activity_id = photoFilters.activity_id.map((a) => a.value);
			}
			if (photoFilters.album_id?.length > 0) {
				params.album_id = photoFilters.album_id.map((a) => a.value);
			}
			if (photoFilters.media_type && photoFilters.media_type.value !== 'all') {
				params.type = photoFilters.media_type.value;
			}
			if (photoFilters.start_date) {
				params.start_date = photoFilters.start_date;
			}
			if (photoFilters.end_date) {
				params.end_date = photoFilters.end_date;
			}

			const queryKey = JSON.stringify(params);
			if (cache.current[queryKey]) {
				setMedia(cache.current[queryKey].data);
				setTotalPages(cache.current[queryKey].totalPages);
				setLoading(false);
				return;
			}

			const res = await getGalleryMedia(params);
			const mediaItems = res.data.data || [];

			cache.current[queryKey] = {
				data: mediaItems,
				totalPages: res.data?.meta?.last_page || res.data?.last_page || 1,
			};
			setMedia(mediaItems);
			setTotalPages(res.data?.meta?.last_page || res.data?.last_page || 1);
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to fetch gallery media', variant: 'error' }));
			setMedia([]);
		} finally {
			setLoading(false);
		}
	}, [page, photoFilters]);

	useEffect(() => {
		if (activeTab === 'photos') {
			fetchMedia();
		}
	}, [fetchMedia, activeTab]);

	// Reset page when filters change
	useEffect(() => {
		setPage(1);
		cache.current = {};
	}, [photoFilters]);

	usePreloadImages(media);

	const handleMediaSelect = (mediaId) => {
		setSelectedMedia((prev) => {
			const next = new Set(prev);
			if (next.has(mediaId)) {
				next.delete(mediaId);
			} else {
				next.add(mediaId);
			}
			return next;
		});
	};

	const handleSelectAll = () => {
		if (selectedMedia.size === media.length) {
			setSelectedMedia(new Set());
		} else {
			setSelectedMedia(new Set(media.map((m) => m.id)));
		}
	};

	const handleAddToAlbumComplete = () => {
		setSelectedMedia(new Set());
	};

	const handleCreateAlbumFromSelection = () => {
		setCreateAlbumOpen(true);
	};

	const handleAlbumCreated = () => {
		setSelectedMedia(new Set());
		setCreateAlbumOpen(false);
	};

	const handleDownload = async () => {
		const mediaIds = Array.from(selectedMedia);
		if (mediaIds.length === 0) return;

		try {
			setDownloadProgress(0);
			setDownloadLink(null);
			setDownloadModalOpen(true);

			const response = await startMediaDownload(mediaIds);
			const jobId = response.data.job_id;

			pollDownloadProgress(jobId);
		} catch (error) {
			const msg = error.response?.data?.message || 'Failed to start download.';
			dispatch(Actions.showMessage({ message: msg, variant: 'error' }));
			setDownloadModalOpen(false);
		}
	};

	const pollDownloadProgress = async (jobId) => {
		try {
			const res = await getMediaDownloadStatus(jobId);
			const { status, progress, download_url, message } = res.data;

			setDownloadProgress(progress);

			if (status === 'completed' && progress === 100) {
				const link = document.createElement('a');
				link.href = download_url;
				link.setAttribute('download', '');
				link.style.display = 'none';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				setDownloadProgress(null);
				setDownloadLink(download_url);
			} else if (status === 'failed') {
				dispatch(Actions.showMessage({ message: message || 'Download failed.', variant: 'error' }));
				setDownloadProgress(null);
				setDownloadModalOpen(false);
			} else {
				setTimeout(() => pollDownloadProgress(jobId), 3000);
			}
		} catch {
			setDownloadProgress(null);
			setDownloadModalOpen(false);
		}
	};

	const updatePhotoFilters = (newFilters) => {
		setPhotoFilters(newFilters);
	};

	const updateAlbumFilters = (newFilters) => {
		setAlbumFilters(newFilters);
	};

	const handleTabChange = (tab) => {
		navigate(tab === 'albums' ? '/gallery/albums' : '/gallery');
		setSelectedMedia(new Set());
	};

	return (
		<div className="gallery-container">
			{/* Tabs */}
			<div className="gallery-tabs px-32">
				<div className="flex">
					<button
						className={`gallery-tab ${activeTab === 'photos' ? 'active' : ''}`}
						onClick={() => handleTabChange('photos')}
						type="button"
					>
						All Photos/Videos
					</button>
					<button
						className={`gallery-tab ${activeTab === 'albums' ? 'active' : ''}`}
						onClick={() => handleTabChange('albums')}
						type="button"
					>
						Albums
					</button>
				</div>
			</div>

			{/* Header */}
			<div className="gallery-header">
				<div>
					<h1>{user?.data?.school?.name || 'School'} Gallery</h1>
					<p>A collection of all photos and videos uploaded to the school.</p>
				</div>
				<div className="gallery-header-actions">
					{activeTab === 'photos' && media.length > 0 && (
						<button
							className="flex items-center gap-4 border border-gray-300 rounded-md px-12 py-8 text-14 text-gray-700 hover:bg-gray-50 cursor-pointer bg-white"
							onClick={handleSelectAll}
							type="button"
						>
							{selectedMedia.size === media.length ? (
								<CheckBox color="primary" style={{ fontSize: 18 }} />
							) : (
								<CheckBoxOutlineBlank style={{ fontSize: 18 }} />
							)}
							Select All
						</button>
					)}
					{activeTab === 'albums' && (
						<div className="flex items-center gap-12">
							<div className="flex items-center border border-gray-300 rounded-md px-10 py-6 bg-white">
								<SearchIcon style={{ fontSize: 18, color: '#9ca3af' }} />
								<input
									className="border-none outline-none text-14 ml-6 bg-transparent"
									onChange={(e) => setAlbumSearchQuery(e.target.value)}
									placeholder="Search albums"
									style={{ width: 160 }}
									type="text"
									value={albumSearchQuery}
								/>
							</div>
							<button
								className="flex items-center gap-8 bg-gray-900 text-white px-16 py-8 rounded-md text-14 font-medium hover:bg-gray-800 transition-colors cursor-pointer"
								onClick={() => setAlbumsCreateOpen(true)}
								type="button"
							>
								<AddIcon style={{ fontSize: 18 }} />
								New Album
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Filters */}
			{activeTab === 'photos' ? (
				<>
					<GalleryFiltersBar filters={photoFilters} setFilters={updatePhotoFilters} tab="photos" />
					<AppliedFiltersBar
						filters={photoFilters}
						hideFilters={['timezone', 'sort', 'dir', 'page']}
						setFilters={updatePhotoFilters}
					/>
				</>
			) : (
				<>
					<GalleryFiltersBar filters={albumFilters} setFilters={updateAlbumFilters} tab="albums" />
					<AppliedFiltersBar
						filters={albumFilters}
						hideFilters={['timezone', 'sort', 'dir', 'page']}
						setFilters={updateAlbumFilters}
					/>
				</>
			)}

			{/* Content */}
			{activeTab === 'photos' ? (
				<div>
					{/* Photo Grid */}
					{loading ? (
						<div className="flex justify-center py-64">
							<CircularProgress size={40} />
						</div>
					) : media.length === 0 ? (
						<div className="text-center py-64">
							<p className="text-gray-500 text-18 mb-8">No photos or videos found</p>
							<p className="text-gray-400 text-14">
								Photos and videos from activities will appear here.
							</p>
						</div>
					) : (
						<>
							<div className="gallery-photo-grid">
								{media.map((item, index) => (
									<div
										className={`gallery-photo-item ${selectedMedia.has(item.id) ? 'selected' : ''}`}
										key={item.id}
									>
										<img
											alt=""
											loading="lazy"
											onClick={() => setLightboxIndex(index)}
											src={item.thumb || item.link}
										/>
										<div className="photo-checkbox">
											<input
												checked={selectedMedia.has(item.id)}
												onChange={() => handleMediaSelect(item.id)}
												onClick={(e) => e.stopPropagation()}
												type="checkbox"
											/>
										</div>
										{item.type === 'video' && (
											<div className="video-duration">Video</div>
										)}
									</div>
								))}
							</div>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className="gallery-pagination">
									<Pagination
										boundaryCount={1}
										color="primary"
										count={totalPages}
										onChange={(_, value) => setPage(value)}
										page={page}
										shape="rounded"
										siblingCount={0}
									/>
								</div>
							)}
						</>
					)}
				</div>
			) : (
				<GalleryAlbums
					createModalOpen={albumsCreateOpen}
					filters={albumFilters}
					searchQuery={albumSearchQuery}
					setCreateModalOpen={setAlbumsCreateOpen}
				/>
			)}

			{/* Selection Bar */}
			{selectedMedia.size > 0 && (
				<div className="gallery-selection-bar">
					<button onClick={() => setSelectedMedia(new Set())} type="button">
						&#x2715;
					</button>
					<span className="selection-count">{selectedMedia.size}</span>
					<span className="selection-label">Items Selected</span>
					<button onClick={() => setAddToAlbumOpen(true)} type="button">
						Add to Album
					</button>
					<button onClick={handleDownload} type="button">
						Download
					</button>
				</div>
			)}

			{/* Lightbox */}
			{lightboxIndex !== null && media.length > 0 && (
				<Lightbox
					initialIndex={lightboxIndex}
					media={media}
					onClose={() => setLightboxIndex(null)}
				/>
			)}

			{/* Add to Album Modal */}
			<AddToAlbumModal
				mediaIds={Array.from(selectedMedia)}
				onComplete={handleAddToAlbumComplete}
				onCreateNew={handleCreateAlbumFromSelection}
				open={addToAlbumOpen}
				setOpen={setAddToAlbumOpen}
			/>

			{/* Create Album Modal (from selection) */}
			<CreateAlbumModal
				initialMediaIds={Array.from(selectedMedia)}
				onAlbumCreated={handleAlbumCreated}
				open={createAlbumOpen}
				setOpen={setCreateAlbumOpen}
			/>

			{/* Download Progress Modal */}
			<DownloadProgressModal
				downloadLink={downloadLink}
				open={downloadModalOpen}
				progress={downloadProgress}
				setDownloadLink={setDownloadLink}
				setOpen={setDownloadModalOpen}
			/>
		</div>
	);
};

export default Gallery;
